import type { CurrentSession } from '@elite-residence/api-client';
import { type ReactNode, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { RequireSession, useSignedOut } from '../auth/session';
import { businessDate } from '../dates';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Icon, type IconName, Logo, LogoMark } from '../ui/icons';
import { GlobalSearch } from './GlobalSearch';

// The signed-in application frame (approved prototype layout): a brand
// sidebar with grouped navigation, a top bar with the business date, the
// language switch and the account actions. Navigation shows only what the
// Staff member's own effective permissions allow; the server rechecks
// every action.

export interface NavItem {
  readonly to: string;
  readonly icon: IconName;
  readonly label: Readonly<Record<'ar' | 'en', string>>;
  // Shown when the session holds any of these permission keys (none = all).
  readonly anyOf?: readonly string[];
}

export interface NavGroup {
  readonly label: Readonly<Record<'ar' | 'en', string>>;
  readonly items: readonly NavItem[];
}

const FINANCE_KEYS = [
  'finance.booking_view',
  'finance.pending_manage',
  'finance.noncash_verify',
  'finance.rent_payment_record',
  'finance.rent_refund',
  'finance.deposit_collection_record',
  'finance.deposit_refund',
  'finance.deposit_retained',
];

// Feature modules register their entries here.
export const NAVIGATION: readonly NavGroup[] = [
  {
    label: { en: 'Operations', ar: 'العمليات' },
    items: [
      { to: '/', icon: 'grid', label: { en: 'Dashboard', ar: 'لوحة المعلومات' } },
      { to: '/board', icon: 'board', label: { en: 'Unit board', ar: 'لوحة الوحدات' }, anyOf: ['booking.view'] },
      { to: '/bookings', icon: 'calendar', label: { en: 'Bookings', ar: 'الحجوزات' }, anyOf: ['booking.view'] },
      { to: '/operations', icon: 'clock', label: { en: 'Today', ar: 'اليوم' }, anyOf: ['booking.view'] },
      { to: '/customers', icon: 'users', label: { en: 'Customers', ar: 'العملاء' }, anyOf: ['customer.basic_view'] },
      {
        to: '/maintenance',
        icon: 'wrench',
        label: { en: 'Maintenance', ar: 'الصيانة' },
        anyOf: ['maintenance.create_edit', 'maintenance.planning', 'maintenance.close', 'maintenance.cancel_planned'],
      },
    ],
  },
  {
    label: { en: 'Finance', ar: 'المالية' },
    items: [
      { to: '/finance', icon: 'wallet', label: { en: 'Finance', ar: 'المالية' }, anyOf: FINANCE_KEYS },
      {
        to: '/expenses',
        icon: 'receipt',
        label: { en: 'Expenses', ar: 'المصروفات' },
        anyOf: ['expense.view', 'expense.record', 'expense.supplier_refund'],
      },
    ],
  },
  {
    label: { en: 'Management', ar: 'الإدارة' },
    items: [
      { to: '/contracts', icon: 'file', label: { en: 'Contracts', ar: 'العقود' }, anyOf: ['contract.view'] },
      { to: '/reports', icon: 'chart', label: { en: 'Reports', ar: 'التقارير' }, anyOf: ['booking.view'] },
      {
        to: '/team',
        icon: 'badge',
        label: { en: 'Team & permissions', ar: 'الفريق والصلاحيات' },
        anyOf: ['team.view'],
      },
      { to: '/audit', icon: 'shield', label: { en: 'Audit', ar: 'السجل' }, anyOf: ['audit.view'] },
      {
        to: '/property',
        icon: 'building',
        label: { en: 'Property setup', ar: 'تهيئة العقار' },
        anyOf: ['property.setup', 'property.reference_price', 'unit.archive'],
      },
      {
        to: '/reference-data',
        icon: 'settings',
        label: { en: 'Reference data', ar: 'البيانات المرجعية' },
        anyOf: ['reference_data.manage'],
      },
    ],
  },
];

const ROLE_LABEL: Readonly<Record<CurrentSession['role'], Readonly<Record<'ar' | 'en', string>>>> = {
  ceo: { en: 'CEO', ar: 'الرئيس التنفيذي' },
  manager: { en: 'Manager', ar: 'المدير' },
  accountant: { en: 'Accountant', ar: 'المحاسب' },
  booking_responsible: { en: 'Booking Responsible', ar: 'مسؤول الحجز' },
  operations: { en: 'Operations', ar: 'العمليات' },
};

const text: Dictionary<
  'account' | 'signOut' | 'mainNavigation' | 'menu' | 'oneBranch' | 'allBranches' | 'riyadh' | 'language'
> = {
  en: {
    account: 'My account',
    signOut: 'Sign out',
    mainNavigation: 'Main navigation',
    menu: 'Menu',
    oneBranch: 'One branch',
    allBranches: 'All branches',
    riyadh: 'Riyadh time',
    language: 'Language',
  },
  ar: {
    account: 'حسابي',
    signOut: 'تسجيل الخروج',
    mainNavigation: 'التنقل الرئيسي',
    menu: 'القائمة',
    oneBranch: 'فرع واحد',
    allBranches: 'كل الفروع',
    riyadh: 'توقيت الرياض',
    language: 'اللغة',
  },
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word.replace(/^Al-/i, '').charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// The business date (Asia/Riyadh) shown in the top bar, e.g. "Sat 3 Oct 2026".
function formatBusinessDate(locale: 'ar' | 'en', iso: string): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

// One language switch for every frame: the active language is shown as
// selected; the other is the single button (named in its own language).
function LanguageSwitch() {
  const { locale, setLocale } = useLocale();
  const t = useText(text);
  return (
    <fieldset className="er-seg er-seg--sm">
      <legend className="er-visually-hidden">{t.language}</legend>
      {locale === 'en' ? (
        <span className="er-seg__opt is-active" lang="en">
          EN
        </span>
      ) : (
        <button className="er-seg__opt" type="button" lang="en" aria-label="English" onClick={() => setLocale('en')}>
          EN
        </button>
      )}
      {locale === 'ar' ? (
        <span className="er-seg__opt is-active" lang="ar">
          عربي
        </span>
      ) : (
        <button className="er-seg__opt" type="button" lang="ar" aria-label="العربية" onClick={() => setLocale('ar')}>
          عربي
        </button>
      )}
    </fieldset>
  );
}

function Sidebar({ session }: { readonly session: CurrentSession }) {
  const { locale, messages } = useLocale();
  const t = useText(text);
  const allowed = (item: NavItem) => !item.anyOf || item.anyOf.some((key) => session.permissions.includes(key));
  return (
    <aside className="er-sidebar">
      <Link className="er-sidebar__logo" to="/" aria-label={messages.appName}>
        <Logo className="er-logo-full" />
        <LogoMark className="er-logo-mark" />
      </Link>
      <nav aria-label={t.mainNavigation}>
        {NAVIGATION.map((group) => {
          const items = group.items.filter(allowed);
          if (items.length === 0) return null;
          return (
            <div key={group.label.en}>
              <div className="er-navgroup" aria-hidden="true">
                {group.label[locale]}
              </div>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  className="er-navitem"
                  to={item.to}
                  end={item.to === '/'}
                  title={item.label[locale]}
                >
                  <Icon name={item.icon} />
                  <span className="er-navitem__label">{item.label[locale]}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <Link className="er-sidebar__user" to="/account" title={t.account}>
        <span className="er-avatar" aria-hidden="true">
          {initials(session.displayName)}
        </span>
        <span className="er-sidebar__who" style={{ minWidth: 0 }}>
          <span className="er-sidebar__name" style={{ display: 'block' }} data-testid="signed-in-name">
            {session.displayName}
          </span>
          <span className="er-sidebar__role" style={{ display: 'block' }}>
            {ROLE_LABEL[session.role][locale]} · {session.scope.type === 'all_branches' ? t.allBranches : t.oneBranch}
          </span>
        </span>
      </Link>
    </aside>
  );
}

function Topbar({ session, onMenu }: { readonly session: CurrentSession; readonly onMenu: () => void }) {
  const { locale } = useLocale();
  const t = useText(text);
  const client = useApiClient();
  const signedOut = useSignedOut();

  async function signOut() {
    await client.logout({ body: {} }).catch(() => undefined);
    signedOut();
    // A full navigation discards every in-memory result of the session.
    window.location.replace('/login');
  }

  return (
    <header className="er-topbar">
      <button
        className="er-btn er-btn--ghost er-btn--icon er-menubtn"
        type="button"
        aria-label={t.menu}
        onClick={onMenu}
      >
        <Icon name="menu" />
      </button>
      {['booking.view', 'customer.basic_view'].some((key) => session.permissions.includes(key)) ? (
        <GlobalSearch session={session} />
      ) : null}
      <span className="er-topbar__spacer" />
      <span className="er-topbar__date">
        {formatBusinessDate(locale, businessDate(new Date()))} · {t.riyadh}
      </span>
      <LanguageSwitch />
      <Link className="er-btn er-btn--ghost er-btn--sm" to="/account">
        <Icon name="user" />
        <span className="er-topbar__label">{t.account}</span>
      </Link>
      <button className="er-btn er-btn--secondary er-btn--sm" type="button" onClick={() => void signOut()}>
        <Icon name="logout" className="er-flip" />
        <span className="er-topbar__label">{t.signOut}</span>
      </button>
    </header>
  );
}

function AppFrame({ session, children }: { readonly session: CurrentSession; readonly children: ReactNode }) {
  // The mobile menu stays open only on the page where it was opened, so a
  // navigation closes it.
  const location = useLocation();
  const [openOn, setOpenOn] = useState<string | null>(null);
  const navOpen = openOn === location.pathname;
  const setNavOpen = (open: boolean | ((current: boolean) => boolean)) => {
    const next = typeof open === 'function' ? open(navOpen) : open;
    setOpenOn(next ? location.pathname : null);
  };
  return (
    <div className={navOpen ? 'er-app nav-open' : 'er-app'}>
      <Sidebar session={session} />
      <div className="er-shell-main">
        <Topbar session={session} onMenu={() => setNavOpen((open) => !open)} />
        <div className="er-page">{children}</div>
      </div>
      {navOpen ? <div className="er-scrim" aria-hidden="true" onClick={() => setNavOpen(false)} /> : null}
    </div>
  );
}

// A protected page inside the frame.
export function SignedIn({ children }: { readonly children: (session: CurrentSession) => ReactNode }) {
  return <RequireSession>{(session) => <AppFrame session={session}>{children(session)}</AppFrame>}</RequireSession>;
}

// Public pages (sign-in flows, contract signing) keep the brand bar and the
// language switch.
export function PublicFrame({ children }: { readonly children: ReactNode }) {
  const { messages } = useLocale();
  return (
    <div className="er-public">
      <header className="er-public__bar">
        <Logo className="er-logo" />
        <span className="er-visually-hidden">{messages.appName}</span>
        <span className="er-topbar__spacer" />
        <LanguageSwitch />
      </header>
      <div className="er-public__body">{children}</div>
    </div>
  );
}
